import { useEffect, useRef, useState } from 'react'

/**
 * Déclenche un compteur animé (0 → `end`) la première fois que l'élément
 * entre dans le viewport. Renvoie la ref à attacher et la valeur courante.
 * Respecte prefers-reduced-motion (affiche directement la valeur finale).
 */
export function useInViewCount(end: number, duration = 1400) {
  const ref = useRef<HTMLElement | null>(null)
  const [value, setValue] = useState(0)
  const done = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce) {
      setValue(end)
      return
    }

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !done.current) {
          done.current = true
          const start = performance.now()
          const tick = (now: number) => {
            const p = Math.min(1, (now - start) / duration)
            // easeOutCubic
            const eased = 1 - Math.pow(1 - p, 3)
            setValue(end * eased)
            if (p < 1) requestAnimationFrame(tick)
            else setValue(end)
          }
          requestAnimationFrame(tick)
          obs.disconnect()
        }
      },
      { threshold: 0.4 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [end, duration])

  return { ref, value }
}
